import { i18n } from '@boot/i18n.js';
import { convertToUnitSystem } from '@models/products.js';
import {
    formatNumber,
    formatProductionValue,
    roundTo
} from '@utils/numberFormatters.js';
import { hasSelectedAssets, hasSelectedShifts } from './../filters.js';
import { orderRows } from './../utils.js';
import {
    calcAvailability,
    calcNetProductionCount,
    calcOEE,
    calcPerformance,
    calcPlannedProduction,
    calcProductionRate,
    calcQuality,
    calcTEEP,
    calcUtilization
} from './metrics.js';

/**
 * Determine whether a record should be included in the report.
 * @return {Boolean}
 */
export function isIncluded(record, filters) {
    return (
        hasSelectedAssets(record, filters) && hasSelectedShifts(record, filters)
    );
}

/**
 * Add value for a multi-unit entry.
 * @param {Object} multiUnitField - e.g. object that looks like { L: 0, kg: 0 }
 * @param {String} unitMeasure - e.g. 'kg' or 'litre'
 * @param {Number} value - amount to increment the field with the given unit by
 * @param {Number} unit_packing - scale factor for the value
 * @return {Object}
 */
export function addByUnitMeasure(
    multiUnitField,
    unitMeasure,
    value,
    unit_packing
) {
    switch (unitMeasure) {
        case 'litre':
            multiUnitField['L'] += value * unit_packing;
            break;
        case 'kg':
            multiUnitField['kg'] += value * unit_packing;
            break;
    }
    return multiUnitField;
}

/**
 * Get the default value for multi-unit entries in the table.
 * @return {Object}
 */
export function getDefaultUnitQuantities() {
    return { L: 0, kg: 0 };
}

/**
 * Get default values for a new asset/product row in the performance table.
 * @return {Object}
 */
export function getDefaultRowValues() {
    return {
        quality: {
            totalProduction: 0,
            rejectProduction: 0,
            netProduction: 0,
            totalQuantity: getDefaultUnitQuantities(),
            rejectQuantity: getDefaultUnitQuantities(),
            netQuantity: getDefaultUnitQuantities(),
            quality: 0
        },
        performance: {
            runningDuration: 0,
            perfDowntimeDuration: 0,
            plannedProduction: 0,
            actualRate: 0,
            plannedRate: 0,
            performance: 0
        },
        availability: {
            running: 0,
            changeoverDuration: 0,
            totalDowntime: 0,
            availabilityDowntime: 0,
            performanceDowntime: 0,
            unplannedDowntime: 0,
            plannedDowntime: 0,
            availability: 0
        },
        oee: 0,
        // Total Effective Equipment Performance
        teep: 0
    };
}

/**
 * Updates the values of a row object with the data from a record object.
 *
 * @param {Object} row - The row object to be updated.
 * @param {Object} record - The record object containing new data.
 */
export function updateRowValues(row, record) {
    // aggregate or update quality fields
    const rowQuality = row['quality'];
    rowQuality.totalProduction += record.production;
    rowQuality.rejectProduction += record.rejected_production;
    const recordNetProduction = calcNetProductionCount(
        record.production,
        record.rejected_production
    );
    rowQuality.netProduction = calcNetProductionCount(
        rowQuality.totalProduction,
        rowQuality.rejectProduction
    );
    rowQuality.totalQuantity = addByUnitMeasure(
        rowQuality.totalQuantity,
        record.unit_measure,
        record.production,
        record.unit_packing
    );
    rowQuality.rejectQuantity = addByUnitMeasure(
        rowQuality.rejectQuantity,
        record.unit_measure,
        record.rejected_production,
        record.unit_packing
    );
    rowQuality.netQuantity = addByUnitMeasure(
        rowQuality.netQuantity,
        record.unit_measure,
        recordNetProduction,
        record.unit_packing
    );
    rowQuality.quality = calcQuality(
        rowQuality.totalProduction,
        rowQuality.rejectProduction
    );

    // aggregate or update performance fields
    const rowPerf = row['performance'];
    rowPerf.perfDowntimeDuration += record.performance_downtime_duration;
    rowPerf.runningDuration += record.running_duration;

    // calculate the planned production for the record. It's equal to the
    // planned rate * operating time
    const recordOperatingTime =
        record.running_duration + record.performance_downtime_duration;
    rowPerf.plannedProduction += calcPlannedProduction(
        record.planned_rate,
        recordOperatingTime
    );

    // Update the actual and planned rate for the current row
    // this is necessary as the row can contain a rate calculated over multiple products
    const rowOperatingTime =
        rowPerf.runningDuration + rowPerf.perfDowntimeDuration;
    rowPerf.actualRate = calcProductionRate(
        rowQuality.totalProduction,
        rowOperatingTime
    );
    rowPerf.plannedRate = calcProductionRate(
        rowPerf.plannedProduction,
        rowOperatingTime
    );
    rowPerf.performance = calcPerformance(
        rowPerf.actualRate,
        rowPerf.plannedRate
    );

    // aggregate or update availability fields
    const rowAvailability = row['availability'];
    rowAvailability.running += record.running_duration;
    rowAvailability.changeoverDuration += record.changeover_duration;
    rowAvailability.totalDowntime += record.downtime_duration;
    rowAvailability.availabilityDowntime +=
        record.availability_downtime_duration;
    rowAvailability.performanceDowntime += record.performance_downtime_duration;
    rowAvailability.availability = calcAvailability(
        rowAvailability.availabilityDowntime,
        rowAvailability.running + rowAvailability.performanceDowntime
    );
    rowAvailability.unplannedDowntime +=
        record.downtime_duration - record.actual_planned_downtime_duration;
    rowAvailability.plannedDowntime += record.actual_planned_downtime_duration;

    // update OEE
    row['oee'] = calcOEE(
        rowAvailability.availability,
        rowPerf.performance,
        rowQuality.quality
    );

    // update Total Effective Equipment Performance (TEEP)
    const utilization = calcUtilization(
        record.running_duration,
        record.total_duration
    );
    row['teep'] = calcTEEP(
        utilization,
        rowPerf.performance,
        rowQuality.quality
    );
}

/**
 * Retrieves or initializes an asset row in the report based on the given record.
 *
 * @param {Object} report - The report object containing asset data.
 * @param {Object} record - The record object containing asset information.
 * @param {string} record.asset_id - The unique identifier for the asset.
 * @param {string} record.asset_name - The name of the asset.
 * @returns {Object} The asset row in the report.
 */
export function getAssetRow(report, record) {
    const assetId = record.asset_id;
    if (!(assetId in report.assets)) {
        // initialize row
        report['assets'][assetId] = {
            name: record.asset_name,
            products: {},
            ...getDefaultRowValues()
        };
    }
    return report['assets'][assetId];
}

/**
 * Retrieves or initializes a product row within an asset row based on the provided record.
 *
 * @param {Object} assetRow - The asset row containing product information.
 * @param {Object} record - The record containing product details.
 * @param {string} record.product_id - The unique identifier for the product.
 * @param {string} record.product_name - The name of the product.
 * @param {string} record.sku - The SKU of the product.
 * @returns {Object} The product row within the asset row.
 */
export function getProductRow(assetRow, record) {
    const productId = record.product_id;
    if (!(productId in assetRow['products'])) {
        // initialize row
        assetRow['products'][productId] = {
            name: record.product_name,
            sku: record.sku,
            ...getDefaultRowValues()
        };
    }
    return assetRow['products'][productId];
}

/**
 * Builds a performance report based on the provided records and filters.
 *
 * @param {Array} records - The array of records to process.
 * @param {Object} filters - The filters to apply to the records.
 * @returns {Object} The performance report containing assets and totals.
 */
export function buildPerformanceReport(records, filters) {
    const report = {
        assets: {},
        shiftTotals: {},
        totals: getDefaultRowValues()
    };

    if (records !== null) {
        for (const record of records) {
            if (isIncluded(record, filters)) {
                // Get or initialize the asset and product rows
                const assetRow = getAssetRow(report, record);
                const productRow = getProductRow(assetRow, record);

                // Initialize the 'shifts' object in the product row if it doesn't exist
                if (!productRow['shifts']) {
                    productRow['shifts'] = {};
                }

                // Set the shift data for the current record in the product row
                productRow['shifts'][record.shift_id] = {
                    shiftId: record.shift_id,
                    assetId: record.asset_id,
                    assetName: record.asset_name,
                    name: record.product_name,
                    sku: record.sku,
                    ...getDefaultRowValues()
                };

                // Initialize the 'assetTotals' object in the asset row if it doesn't exist
                if (!assetRow['assetTotals']) {
                    assetRow['assetTotals'] = {};
                }

                // Initialize the asset shift total data for the current record if it doesn't exist
                if (!assetRow['assetTotals'][record.shift_id]) {
                    assetRow['assetTotals'][record.shift_id] = {
                        shiftId: record.shift_id,
                        ...getDefaultRowValues()
                    };
                }

                // Initialize the shift total data for the current record if it doesn't exist
                if (!report['shiftTotals'][record.shift_id]) {
                    report['shiftTotals'][record.shift_id] = {
                        shiftId: record.shift_id,
                        ...getDefaultRowValues()
                    };
                }

                // Update the values
                updateRowValues(assetRow, record);
                updateRowValues(productRow, record);
                updateRowValues(
                    assetRow['assetTotals'][record.shift_id],
                    record
                );
                updateRowValues(productRow['shifts'][record.shift_id], record);
                updateRowValues(report['shiftTotals'][record.shift_id], record);
                updateRowValues(report['totals'], record);
            }
        }
    }
    return report;
}

/**
 * Builds a table structure from the given report data, organizing it by assets and their respective products.
 *
 * @param {Object} report - The report data containing assets and totals.
 * @param {string} orderBy - The criteria to order the rows by.
 * @returns {Object} The constructed table with rows and totals.
 */
export function buildTable(report, orderBy) {
    const table = {
        rows: [],
        totals: report.totals
    };

    const assets = report.assets;
    for (const [assetId, assetData] of Object.entries(assets)) {
        // build asset row
        const row = {
            assetId: parseInt(assetId),
            name: assetData.name,
            quality: {
                totalProduction: assetData.quality.totalProduction,
                totalQuantity: assetData.quality.totalQuantity,
                rejectProduction: assetData.quality.rejectProduction,
                rejectQuantity: assetData.quality.rejectQuantity,
                netProduction: assetData.quality.netProduction,
                netQuantity: assetData.quality.netQuantity,
                quality: assetData.quality.quality
            },
            performance: {
                actualRate: assetData.performance.actualRate,
                plannedRate: assetData.performance.plannedRate,
                performance: assetData.performance.performance
            },
            availability: {
                running: assetData.availability.running,
                changeoverDuration: assetData.availability.changeoverDuration,
                totalDowntime: assetData.availability.totalDowntime,
                availabilityDowntime:
                    assetData.availability.availabilityDowntime,
                performanceDowntime: assetData.availability.performanceDowntime,
                unplannedDowntime: assetData.availability.unplannedDowntime,
                plannedDowntime: assetData.availability.plannedDowntime,
                availability: parseFloat(assetData.availability.availability)
            },
            oee: assetData.oee,
            teep: assetData.teep,
            products: []
        };

        // build product rows
        for (const [productId, productData] of Object.entries(
            assets[assetId].products
        )) {
            const expandedRow = {
                assetId: parseInt(assetId),
                productId: parseInt(productId),
                sku: productData.sku,
                name: productData.name,
                quality: {
                    totalProduction: productData.quality.totalProduction,
                    totalQuantity: productData.quality.totalQuantity,
                    rejectProduction: productData.quality.rejectProduction,
                    rejectQuantity: productData.quality.rejectQuantity,
                    netProduction: productData.quality.netProduction,
                    netQuantity: productData.quality.netQuantity,
                    quality: productData.quality.quality
                },
                performance: {
                    actualRate: productData.performance.actualRate,
                    plannedRate: productData.performance.plannedRate,
                    performance: productData.performance.performance
                },
                availability: {
                    running: productData.availability.running,
                    changeoverDuration:
                        productData.availability.changeoverDuration,
                    totalDowntime: productData.availability.totalDowntime,
                    availabilityDowntime:
                        productData.availability.availabilityDowntime,
                    performanceDowntime:
                        productData.availability.performanceDowntime,
                    unplannedDowntime:
                        productData.availability.unplannedDowntime,
                    plannedDowntime: productData.availability.plannedDowntime,
                    availability: productData.availability.availability
                },
                oee: productData.oee,
                teep: productData.teep
            };
            row.products.push(expandedRow);
        }
        orderRows(row.products, orderBy);
        table.rows.push(row);
    }
    orderRows(table.rows, orderBy);
    return table;
}

/**
 * Generates report data rows for a performance report.
 *
 * @param {Object} data - The data object containing rows and totals.
 * @param {string} unitSystem - The unit system to be used (e.g., 'metric' or 'imperial').
 * @param {boolean} addSubtotals - Flag indicating whether to add subtotals for each asset.
 * @param {string} facilityName - The name of the facility.
 * @param {string} startDate - The start date of the report period.
 * @param {string} endDate - The end date of the report period.
 * @param {Object} shiftIdToNames - Mapping of shift IDs to shift names.
 * @returns {Array} An array of rows representing the report data.
 */
export function getReportDataFileRows(
    data,
    unitSystem,
    addSubtotals,
    facilityName,
    startDate,
    endDate,
    shiftIdToNames
) {
    // Verify that the data has "rows" and "totals" properties
    if (!data || !data.rows || !data.totals) {
        return [];
    }

    const rows = [];

    // Helper function to add a row
    const addRow = (asset, product) => {
        const item = product || asset; // Use product if available, otherwise use asset

        rows.push([
            facilityName, // Facility name
            asset.name, // Asset name or "asset totals"
            product?.sku ? product.sku : i18n.global.t('All SKUs'), // Product SKU or "All SKUs"
            product?.name ? product.name : i18n.global.t('All Products'), // Product name or "All Products"
            product?.shiftId
                ? shiftIdToNames[product.shiftId]
                : i18n.global.t('All Shifts'), // Shift name or "All Shifts"
            formatProductionValue(item.quality.totalProduction), // Total production
            formatQuantities(item.quality.totalQuantity.L, 'L', unitSystem), // Total quantity in liters
            formatQuantities(item.quality.totalQuantity.kg, 'kg', unitSystem), // Total quantity in kilograms
            formatProductionValue(item.quality.rejectProduction), // Reject production
            formatQuantities(item.quality.rejectQuantity.L, 'L', unitSystem), // Reject quantity in liters
            formatQuantities(item.quality.rejectQuantity.kg, 'kg', unitSystem), // Reject quantity in kilograms
            formatProductionValue(item.quality.netProduction), // Net production
            formatQuantities(item.quality.netQuantity.L, 'L', unitSystem), // Net quantity in liters
            formatQuantities(item.quality.netQuantity.kg, 'kg', unitSystem), // Net quantity in kilograms
            roundTo(item.quality.quality, 4), // Quality
            formatNumber(item.performance.actualRate), // Actual rate
            formatNumber(item.performance.plannedRate), // Max run rate
            roundTo(item.performance.performance, 4), // Performance
            formatNumber(item.availability.running / 60), // Running duration
            formatNumber(item.availability.changeoverDuration / 60), // Changeover duration
            formatNumber(item.availability.totalDowntime / 60), // Total downtime
            formatNumber(item.availability.availabilityDowntime / 60), // Availability downtime
            formatNumber(item.availability.performanceDowntime / 60), // Performance downtime
            formatNumber(item.availability.unplannedDowntime / 60), // Unplanned downtime
            formatNumber(item.availability.plannedDowntime / 60), // Planned downtime
            roundTo(item.availability.availability, 4), // Availability
            roundTo(item.oee, 4), // OEE
            startDate, // Start date
            endDate // End date
        ]);
    };

    data.rows.forEach(asset => {
        asset.products.forEach(product => addRow(asset, product)); // Add each product row
        if (addSubtotals) {
            addRow(asset); // Add the asset row containing subtotals
        }
    });

    // Extract the totals
    const { totals } = data;

    // Add the grand total row
    if (data.rows.length > 0 && totals) {
        rows.push([
            facilityName,
            i18n.global.t('All Assets'), // Asset name
            i18n.global.t('All SKUs'), // Product SKU
            i18n.global.t('All Products'), // Product name
            i18n.global.t('All Shifts'), // Shift
            formatProductionValue(totals.quality.totalProduction), // Total production
            formatQuantities(totals.quality.totalQuantity.L, 'L', unitSystem), // Total quantity in liters
            formatQuantities(totals.quality.totalQuantity.kg, 'kg', unitSystem), // Total quantity in kilograms
            formatProductionValue(totals.quality.rejectProduction), // Reject production
            formatQuantities(totals.quality.rejectQuantity.L, 'L', unitSystem), // Reject quantity in liters
            formatQuantities(
                totals.quality.rejectQuantity.kg,
                'kg',
                unitSystem
            ), // Reject quantity in kilograms
            formatProductionValue(totals.quality.netProduction), // Net production
            formatQuantities(totals.quality.netQuantity.L, 'L', unitSystem), // Net quantity in liters
            formatQuantities(totals.quality.netQuantity.kg, 'kg', unitSystem), // Net quantity in kilograms
            roundTo(totals.quality.quality, 4), // Quality
            formatNumber(totals.performance.actualRate), // Actual rate
            formatNumber(totals.performance.plannedRate), // Max run rate
            roundTo(totals.performance.performance, 4), // Performance
            formatNumber(totals.availability.running / 60), // Running duration
            formatNumber(totals.availability.changeoverDuration / 60), // Changeover duration
            formatNumber(totals.availability.totalDowntime / 60), // Total downtime
            formatNumber(totals.availability.availabilityDowntime / 60), // Availability downtime
            formatNumber(totals.availability.performanceDowntime / 60), // Performance downtime
            formatNumber(totals.availability.unplannedDowntime / 60), // Unplanned downtime
            formatNumber(totals.availability.plannedDowntime / 60), // Planned downtime
            roundTo(totals.availability.availability, 4), // Availability
            roundTo(totals.oee, 4), // OEE
            startDate, // Start date
            endDate // End date
        ]);
    }

    return rows;
}

/**
 * Format the production quantities. Convert to the specified unit system if necessary.
 * @param {Number} value - The value to format.
 * @param {String} unit - The unit of the value.
 * @param {String} unitSystem - The unit system to convert to.
 * @return {Number} - The formatted value.
 */
function formatQuantities(value, unit, unitSystem) {
    if (unitSystem === 'imperial') {
        const { value: convertedValue } = convertToUnitSystem(
            value,
            unit,
            'imperial'
        );
        return formatProductionValue(convertedValue);
    }
    return formatProductionValue(value);
}

/**
 * Generates a report data array by shift from the provided data.
 *
 * @param {Object} data - The data object containing assets and totals.
 * @param {string} unitSystem - The unit system to use for formatting quantities.
 * @param {boolean} addSubtotals - Flag indicating whether to add subtotals for each asset.
 * @param {string} facilityName - The name of the facility.
 * @param {string} startDate - The start date of the report.
 * @param {string} endDate - The end date of the report.
 * @param {Object} shiftIdToNames - A mapping of shift IDs to shift names.
 * @returns {Array} An array of rows representing the report data.
 */
export function getReportDataByShift(
    data,
    unitSystem,
    addSubtotals,
    facilityName,
    startDate,
    endDate,
    shiftIdToNames
) {
    // Verify that the data has "assets" and "totals" properties
    if (!data || !data.assets || !data.totals || !data.shiftTotals) {
        return [];
    }

    const rows = [];

    // Helper function to add a shift row
    const addShiftRow = (shift, assetName) => {
        rows.push([
            facilityName, // Facility name
            shift.assetName || assetName, // Asset name
            shift?.sku ? shift.sku : i18n.global.t('All SKUs'), // Product SKU or "All SKUs"
            shift?.name ? shift.name : i18n.global.t('All Products'), // Product name or "All Products"
            shift?.shiftId
                ? shiftIdToNames[shift.shiftId]
                : i18n.global.t('All Shifts'), // Shift name or "All Shifts"
            formatProductionValue(shift.quality.totalProduction), // Total production
            formatQuantities(shift.quality.totalQuantity.L, 'L', unitSystem), // Total quantity in liters
            formatQuantities(shift.quality.totalQuantity.kg, 'kg', unitSystem), // Total quantity in kilograms
            formatProductionValue(shift.quality.rejectProduction), // Reject production
            formatQuantities(shift.quality.rejectQuantity.L, 'L', unitSystem), // Reject quantity in liters
            formatQuantities(shift.quality.rejectQuantity.kg, 'kg', unitSystem), // Reject quantity in kilograms
            formatProductionValue(shift.quality.netProduction), // Net production
            formatQuantities(shift.quality.netQuantity.L, 'L', unitSystem), // Net quantity in liters
            formatQuantities(shift.quality.netQuantity.kg, 'kg', unitSystem), // Net quantity in kilograms
            roundTo(shift.quality.quality, 4), // Quality
            formatNumber(shift.performance.actualRate), // Actual rate
            formatNumber(shift.performance.plannedRate), // Max run rate
            roundTo(shift.performance.performance, 4), // Performance
            formatNumber(shift.availability.running / 60), // Running duration
            formatNumber(shift.availability.changeoverDuration / 60), // Changeover duration
            formatNumber(shift.availability.totalDowntime / 60), // Total downtime
            formatNumber(shift.availability.availabilityDowntime / 60), // Availability downtime
            formatNumber(shift.availability.performanceDowntime / 60), // Performance downtime
            formatNumber(shift.availability.unplannedDowntime / 60), // Unplanned downtime
            formatNumber(shift.availability.plannedDowntime / 60), // Planned downtime
            roundTo(shift.availability.availability, 4), // Availability
            roundTo(shift.oee, 4), // OEE
            startDate, // Start date
            endDate // End date
        ]);
    };

    // Loop through each asset in the data
    for (const assetId in data.assets) {
        const asset = data.assets[assetId];
        // Loop through each product within the asset
        for (const productId in asset.products) {
            const product = asset.products[productId];
            // Loop through each shift within the product
            for (const shiftId in product.shifts) {
                addShiftRow(product.shifts[shiftId], asset.name);
            }
            // If addSubtotals flag is true, add shift totals and product row
            if (addSubtotals) {
                addShiftRow(product, asset.name);
            }
        }

        // If addSubtotals flag is true, add asset totals
        if (addSubtotals) {
            for (const shiftId in asset.assetTotals) {
                addShiftRow(asset.assetTotals[shiftId], asset.name);
            }
            // Store the original asset name in a new property 'assetName'
            asset['assetName'] = asset['name'];
            // Set the 'name' property to null to display "All Products" in the report
            asset['name'] = null;
            // add totals for the asset
            addShiftRow(asset, asset['assetName']);
        }
    }

    // Add shift totals
    for (const shiftId in data.shiftTotals) {
        addShiftRow(data.shiftTotals[shiftId], i18n.global.t('All Assets'));
    }

    // Extract the totals
    const { totals } = data;

    // Add the grand total row
    if (Object.keys(data.assets).length && totals) {
        rows.push([
            facilityName,
            i18n.global.t('All Assets'), // Asset name
            i18n.global.t('All SKUs'), // Product SKU
            i18n.global.t('All Products'), // Product name
            i18n.global.t('All Shifts'), // Shift
            formatProductionValue(totals.quality.totalProduction), // Total production
            formatQuantities(totals.quality.totalQuantity.L, 'L', unitSystem), // Total quantity in liters
            formatQuantities(totals.quality.totalQuantity.kg, 'kg', unitSystem), // Total quantity in kilograms
            formatProductionValue(totals.quality.rejectProduction), // Reject production
            formatQuantities(totals.quality.rejectQuantity.L, 'L', unitSystem), // Reject quantity in liters
            formatQuantities(
                totals.quality.rejectQuantity.kg,
                'kg',
                unitSystem
            ), // Reject quantity in kilograms
            formatProductionValue(totals.quality.netProduction), // Net production
            formatQuantities(totals.quality.netQuantity.L, 'L', unitSystem), // Net quantity in liters
            formatQuantities(totals.quality.netQuantity.kg, 'kg', unitSystem), // Net quantity in kilograms
            roundTo(totals.quality.quality, 4), // Quality
            formatNumber(totals.performance.actualRate), // Actual rate
            formatNumber(totals.performance.plannedRate), // Max run rate
            roundTo(totals.performance.performance, 4), // Performance
            formatNumber(totals.availability.running / 60), // Running duration
            formatNumber(totals.availability.changeoverDuration / 60), // Changeover duration
            formatNumber(totals.availability.totalDowntime / 60), // Total downtime
            formatNumber(totals.availability.availabilityDowntime / 60), // Availability downtime
            formatNumber(totals.availability.performanceDowntime / 60), // Performance downtime
            formatNumber(totals.availability.unplannedDowntime / 60), // Unplanned downtime
            formatNumber(totals.availability.plannedDowntime / 60), // Planned downtime
            roundTo(totals.availability.availability, 4), // Availability
            roundTo(totals.oee, 4), // OEE
            startDate, // Start date
            endDate // End date
        ]);
    }
    return rows;
}
